import React, { useState, useEffect, useCallback } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import { toast, ToastContainer } from 'react-toastify';
import { ethers } from 'ethers';
import {
  TREASURY_CONTRACT_ADDRESS,
  BSC_CHAIN_ID_DECIMAL,
  BSC_EXPLORER_URL,
  BSC_USDT_ADDRESS,
  BSC_NMRC_ADDRESS,
  ERC20_ABI,
  getReadOnlyTreasuryContract,
  getWriteTreasuryContract,
  switchWalletToBsc
} from '../Function/TreasuryContract';
import { canModuleEdit } from '../Function/Permission';

function Treasury() {
  const canEdit = canModuleEdit('treasury');

  // ==========================================
  // Web3 / Wallet States
  // ==========================================
  const [account, setAccount] = useState('');
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isOwnerOfContract, setIsOwnerOfContract] = useState(false);
  const [isDeployerOfContract, setIsDeployerOfContract] = useState(false);

  // ==========================================
  // Contract Read States
  // ==========================================
  const [loadingContractData, setLoadingContractData] = useState(true);
  const [contractDeployer, setContractDeployer] = useState('');
  const [ownersList, setOwnersList] = useState([]);
  const [requiredConfirmations, setRequiredConfirmations] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);
  const [bnbBalance, setBnbBalance] = useState('0.0');
  const [nmrcBalance, setNmrcBalance] = useState('0.0');
  const [transactions, setTransactions] = useState([]);

  // Active Tab
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' | 'submit' | 'owners' | 'tokenCheck' | 'deployer' | 'deposit'

  // If Viewer Admin, prevent staying on deployer tab
  useEffect(() => {
    if (!canEdit && activeTab === 'deployer') {
      setActiveTab('transactions');
    }
  }, [canEdit, activeTab]);

  // ==========================================
  // Form / Action States
  // ==========================================
  // 1. Submit BNB / Token Tx
  const [submitTxType, setSubmitTxType] = useState('BNB'); // 'BNB' | 'TOKEN'
  const [submitToAddress, setSubmitToAddress] = useState('');
  const [submitBnbAmount, setSubmitBnbAmount] = useState('');
  const [submitTokenAddress, setSubmitTokenAddress] = useState(BSC_NMRC_ADDRESS);
  const [submitTokenAmount, setSubmitTokenAmount] = useState('');
  const [submitTokenDecimals, setSubmitTokenDecimals] = useState(18);
  const [submitData, setSubmitData] = useState('0x');
  const [isSubmittingTx, setIsSubmittingTx] = useState(false);

  // 2. Direct Deposit BNB
  const [depositAmount, setDepositAmount] = useState('');
  const [isDepositingBnb, setIsDepositingBnb] = useState(false);

  // 3. Token Balance Checker
  const [tokenCheckAddress, setTokenCheckAddress] = useState(BSC_NMRC_ADDRESS);
  const [tokenCheckResult, setTokenCheckResult] = useState(null);
  const [isCheckingToken, setIsCheckingToken] = useState(false);

  // 4. Custom Address Owner Check
  const [verifyOwnerAddress, setVerifyOwnerAddress] = useState('');
  const [verifyOwnerResult, setVerifyOwnerResult] = useState(null);
  const [isVerifyingOwner, setIsVerifyingOwner] = useState(false);

  // 5. Deployer Management
  const [newOwnerAddress, setNewOwnerAddress] = useState('');
  const [removeOwnerAddress, setRemoveOwnerAddress] = useState('');
  const [newRequirementNumber, setNewRequirementNumber] = useState('');
  const [newDeployerAddress, setNewDeployerAddress] = useState('');
  const [isDeployerActionLoading, setIsDeployerActionLoading] = useState(false);

  // 6. Action Execution tracking
  const [actionLoadingTxId, setActionLoadingTxId] = useState(null);

  // Shorten Address Helper
  const shortenAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Copy to clipboard
  const copyText = (txt, label = 'Address') => {
    if (!txt) return;
    navigator.clipboard.writeText(txt).then(() => {
      toast.success(`${label} copied!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  // ==========================================
  // Fetch Contract State (Read-Only RPC)
  // ==========================================
  const fetchContractState = useCallback(async () => {
    setLoadingContractData(true);
    try {
      const contract = getReadOnlyTreasuryContract();

      // Read contract core state
      const [deployerRes, ownersRes, reqConfirmationsRes, txCountRes, bnbBalRes, nmrcBalRes] = await Promise.all([
        contract.deployer(),
        contract.getOwners(),
        contract.requiredConfirmations(),
        contract.getTransactionCount(),
        contract.getBNBBalance(),
        contract.getTokenBalance(BSC_NMRC_ADDRESS).catch(async () => {
          try {
            const nmrcContract = new ethers.Contract(BSC_NMRC_ADDRESS, ERC20_ABI, contract.runner);
            return await nmrcContract.balanceOf(TREASURY_CONTRACT_ADDRESS);
          } catch (e) {
            return 0n;
          }
        })
      ]);

      const dep = deployerRes;
      const ownersArr = Array.from(ownersRes);
      const req = Number(reqConfirmationsRes);
      const count = Number(txCountRes);
      const bnbBal = ethers.formatEther(bnbBalRes);
      const nmrcBal = ethers.formatUnits(nmrcBalRes || 0n, 18);

      setContractDeployer(dep);
      setOwnersList(ownersArr);
      setRequiredConfirmations(req);
      setTransactionCount(count);
      setBnbBalance(bnbBal);
      setNmrcBalance(nmrcBal);

      // Check connected account status
      if (account) {
        const lowerAcc = account.toLowerCase();
        setIsDeployerOfContract(dep.toLowerCase() === lowerAcc);
        const isOwner = ownersArr.some((o) => o.toLowerCase() === lowerAcc);
        setIsOwnerOfContract(isOwner);
      } else {
        setIsDeployerOfContract(false);
        setIsOwnerOfContract(false);
      }

      // Fetch All Transactions (or last 50)
      if (count > 0) {
        const txList = [];
        // Fetch in reverse order (newest first)
        const fetchLimit = Math.min(count, 50);
        for (let i = count - 1; i >= count - fetchLimit; i--) {
          try {
            const t = await contract.getTransaction(i);
            let userConfirmed = false;
            if (account) {
              try {
                userConfirmed = await contract.isConfirmed(i, account);
              } catch (e) {
                userConfirmed = false;
              }
            }

            txList.push({
              txId: i,
              to: t[0],
              value: t[1],
              token: t[2],
              tokenAmount: t[3],
              data: t[4],
              executed: t[5],
              numConfirmations: Number(t[6]),
              isBnb: t[2] === ethers.ZeroAddress,
              userConfirmed
            });
          } catch (txErr) {
            console.error(`Failed to load tx #${i}`, txErr);
          }
        }
        setTransactions(txList);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error reading Treasury contract:', err);
      toast.error('Failed to load Treasury contract state from BSC RPC: ' + (err.reason || err.message));
    } finally {
      setLoadingContractData(false);
    }
  }, [account]);

  useEffect(() => {
    fetchContractState();
  }, [fetchContractState]);

  // ==========================================
  // Web3 Wallet Connection Logic
  // ==========================================
  const checkInitialWallet = async () => {
    const isManuallyDisconnected = localStorage.getItem('treasury_wallet_disconnected') === 'true';
    if (isManuallyDisconnected) {
      return;
    }
    if (window.ethereum) {
      try {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await provider.listAccounts();
        if (accounts.length > 0) {
          const acc = accounts[0].address;
          setAccount(acc);
          const network = await provider.getNetwork();
          setChainId(Number(network.chainId));
        }
      } catch (err) {
        console.error('Error inspecting wallet:', err);
      }
    }
  };

  useEffect(() => {
    checkInitialWallet();

    if (window.ethereum) {
      const handleAccountsChanged = (accs) => {
        if (accs.length === 0) {
          setAccount('');
          setIsOwnerOfContract(false);
          setIsDeployerOfContract(false);
          toast.info('Wallet disconnected');
        } else {
          setAccount(accs[0]);
          localStorage.removeItem('treasury_wallet_disconnected');
          toast.info(`Connected to ${shortenAddress(accs[0])}`);
        }
      };

      const handleChainChanged = (cId) => {
        const parsed = parseInt(cId, 16);
        setChainId(parsed);
        if (parsed !== BSC_CHAIN_ID_DECIMAL) {
          toast.warn('Please switch network to BNB Smart Chain Mainnet (Chain 56)');
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []);

  const connectWallet = async () => {
    if (!window.ethereum) {
      toast.error('No Web3 wallet detected! Please install MetaMask or Trust Wallet.');
      return;
    }

    setIsConnecting(true);
    try {
      localStorage.removeItem('treasury_wallet_disconnected');
      // 1. Request Accounts
      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);
      if (accounts && accounts.length > 0) {
        setAccount(accounts[0]);
        // 2. Check Chain ID
        const network = await provider.getNetwork();
        const currentChain = Number(network.chainId);
        setChainId(currentChain);

        if (currentChain !== BSC_CHAIN_ID_DECIMAL) {
          try {
            await switchWalletToBsc();
            setChainId(BSC_CHAIN_ID_DECIMAL);
            toast.success('Connected and switched to BNB Smart Chain!');
          } catch (switchErr) {
            toast.warn('Connected, but please switch your network to BNB Smart Chain (Chain 56)');
          }
        } else {
          toast.success(`Wallet connected: ${shortenAddress(accounts[0])}`);
        }
      }
    } catch (err) {
      console.error('Wallet connection error:', err);
      toast.error(err.message || 'Failed to connect wallet');
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    localStorage.setItem('treasury_wallet_disconnected', 'true');
    setAccount('');
    setChainId(null);
    setIsOwnerOfContract(false);
    setIsDeployerOfContract(false);
    toast.info('Wallet successfully disconnected');
  };

  // Helper to ensure signer on BSC
  const getSignerContract = async () => {
    if (!canModuleEdit('treasury')) {
      throw new Error('Permission Denied: Your admin role only has view-only access.');
    }
    if (!window.ethereum) {
      throw new Error('Please install MetaMask or a Web3 wallet.');
    }
    if (!account) {
      throw new Error('Please connect your Web3 wallet first.');
    }
    const provider = new ethers.BrowserProvider(window.ethereum);
    const network = await provider.getNetwork();
    if (Number(network.chainId) !== BSC_CHAIN_ID_DECIMAL) {
      await switchWalletToBsc();
    }
    const signer = await provider.getSigner();
    return getWriteTreasuryContract(signer);
  };

  // ==========================================
  // WRITE 1: SUBMIT TRANSACTION
  // ==========================================
  const handleSubmitTransaction = async (e) => {
    e.preventDefault();
    if (!submitToAddress || !ethers.isAddress(submitToAddress)) {
      return toast.error('Please enter a valid recipient address (0x...)');
    }

    setIsSubmittingTx(true);
    try {
      const contract = await getSignerContract();
      const callData = submitData && submitData.startsWith('0x') ? submitData : '0x';

      let tx;
      if (submitTxType === 'BNB') {
        if (!submitBnbAmount || isNaN(submitBnbAmount) || Number(submitBnbAmount) <= 0) {
          setIsSubmittingTx(false);
          return toast.error('Please enter a valid BNB amount');
        }
        const valueWei = ethers.parseEther(submitBnbAmount.toString());
        toast.info('Submitting BNB transaction to blockchain...');
        tx = await contract.submitTransaction(submitToAddress, valueWei, callData);
      } else {
        if (!submitTokenAddress || !ethers.isAddress(submitTokenAddress)) {
          setIsSubmittingTx(false);
          return toast.error('Please enter a valid ERC20 token address');
        }
        if (!submitTokenAmount || isNaN(submitTokenAmount) || Number(submitTokenAmount) <= 0) {
          setIsSubmittingTx(false);
          return toast.error('Please enter a valid token amount');
        }
        const tokenWei = ethers.parseUnits(submitTokenAmount.toString(), submitTokenDecimals || 18);
        toast.info('Submitting Token transaction to blockchain...');
        tx = await contract.submitTokenTransaction(submitToAddress, submitTokenAddress, tokenWei, callData);
      }

      toast.info(`Transaction broadcasted: ${shortenAddress(tx.hash)}. Waiting for block confirmation...`);
      await tx.wait();
      toast.success('MultiSig Transaction successfully submitted!');

      // Reset form & reload state
      setSubmitToAddress('');
      setSubmitBnbAmount('');
      setSubmitTokenAmount('');
      setSubmitData('0x');
      setActiveTab('transactions');
      await fetchContractState();
    } catch (err) {
      console.error('Submit transaction failed:', err);
      toast.error(err.reason || err.message || 'Transaction submission failed');
    } finally {
      setIsSubmittingTx(false);
    }
  };

  // ==========================================
  // WRITE 2: CONFIRM TRANSACTION
  // ==========================================
  const handleConfirmTransaction = async (txId) => {
    setActionLoadingTxId(txId);
    try {
      const contract = await getSignerContract();
      toast.info(`Confirming Tx #${txId}...`);
      const tx = await contract.confirmTransaction(txId);
      await tx.wait();
      toast.success(`Tx #${txId} confirmed successfully!`);
      await fetchContractState();
    } catch (err) {
      console.error('Confirm error:', err);
      toast.error(err.reason || err.message || 'Confirm transaction failed');
    } finally {
      setActionLoadingTxId(null);
    }
  };

  // ==========================================
  // WRITE 3: REVOKE CONFIRMATION
  // ==========================================
  const handleRevokeConfirmation = async (txId) => {
    setActionLoadingTxId(txId);
    try {
      const contract = await getSignerContract();
      toast.info(`Revoking confirmation for Tx #${txId}...`);
      const tx = await contract.revokeConfirmation(txId);
      await tx.wait();
      toast.success(`Confirmation for Tx #${txId} revoked!`);
      await fetchContractState();
    } catch (err) {
      console.error('Revoke error:', err);
      toast.error(err.reason || err.message || 'Revoke confirmation failed');
    } finally {
      setActionLoadingTxId(null);
    }
  };

  // ==========================================
  // WRITE 4: EXECUTE TRANSACTION
  // ==========================================
  const handleExecuteTransaction = async (txId) => {
    setActionLoadingTxId(txId);
    try {
      const contract = await getSignerContract();
      toast.info(`Executing Tx #${txId}...`);
      const tx = await contract.executeTransaction(txId);
      await tx.wait();
      toast.success(`Tx #${txId} executed successfully! Funds transferred.`);
      await fetchContractState();
    } catch (err) {
      console.error('Execute error:', err);
      toast.error(err.reason || err.message || 'Execute transaction failed');
    } finally {
      setActionLoadingTxId(null);
    }
  };

  // ==========================================
  // WRITE 5: DIRECT BNB DEPOSIT
  // ==========================================
  const handleDepositBnb = async (e) => {
    e.preventDefault();
    if (!depositAmount || isNaN(depositAmount) || Number(depositAmount) <= 0) {
      return toast.error('Please enter a valid BNB deposit amount');
    }

    setIsDepositingBnb(true);
    try {
      if (!account) {
        throw new Error('Please connect your wallet first');
      }
      const provider = new ethers.BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== BSC_CHAIN_ID_DECIMAL) {
        await switchWalletToBsc();
      }
      const signer = await provider.getSigner();

      toast.info(`Sending ${depositAmount} BNB to Treasury contract...`);
      const tx = await signer.sendTransaction({
        to: TREASURY_CONTRACT_ADDRESS,
        value: ethers.parseEther(depositAmount.toString())
      });
      await tx.wait();
      toast.success(`Successfully deposited ${depositAmount} BNB to Treasury!`);
      setDepositAmount('');
      await fetchContractState();
    } catch (err) {
      console.error('Deposit BNB error:', err);
      toast.error(err.reason || err.message || 'Deposit failed');
    } finally {
      setIsDepositingBnb(false);
    }
  };

  // ==========================================
  // READ: CHECK ERC20 TOKEN BALANCE
  // ==========================================
  const handleCheckTokenBalance = async (e) => {
    if (e) e.preventDefault();
    if (!tokenCheckAddress || !ethers.isAddress(tokenCheckAddress)) {
      return toast.error('Please enter a valid ERC20 token address');
    }

    setIsCheckingToken(true);
    try {
      const contract = getReadOnlyTreasuryContract();
      const tokenBalWei = await contract.getTokenBalance(tokenCheckAddress);

      // Try fetching token details (symbol, decimals) via fallback provider
      let symbol = 'TOKEN';
      let decimals = 18;
      try {
        const tokenContract = new ethers.Contract(tokenCheckAddress, ERC20_ABI, contract.runner);
        symbol = await tokenContract.symbol();
        decimals = Number(await tokenContract.decimals());
      } catch (metaErr) {
        // Fallback default
      }

      const formatted = ethers.formatUnits(tokenBalWei, decimals);
      setTokenCheckResult({
        address: tokenCheckAddress,
        symbol,
        decimals,
        raw: tokenBalWei.toString(),
        formatted
      });
      toast.success(`Balance: ${formatted} ${symbol}`);
    } catch (err) {
      console.error('Check token error:', err);
      toast.error('Failed to query token balance: ' + (err.reason || err.message));
    } finally {
      setIsCheckingToken(false);
    }
  };

  // ==========================================
  // READ: VERIFY CUSTOM ADDRESS OWNER STATUS
  // ==========================================
  const handleVerifyOwner = async (e) => {
    if (e) e.preventDefault();
    if (!verifyOwnerAddress || !ethers.isAddress(verifyOwnerAddress)) {
      return toast.error('Please enter a valid address to verify');
    }

    setIsVerifyingOwner(true);
    try {
      const contract = getReadOnlyTreasuryContract();
      const isOwner = await contract.isOwner(verifyOwnerAddress);
      setVerifyOwnerResult({
        address: verifyOwnerAddress,
        isOwner
      });
      if (isOwner) {
        toast.success(`Address IS verified as a MultiSig Owner!`);
      } else {
        toast.info(`Address is NOT an Owner.`);
      }
    } catch (err) {
      toast.error('Verify failed: ' + (err.reason || err.message));
    } finally {
      setIsVerifyingOwner(false);
    }
  };

  // ==========================================
  // WRITE 6: DEPLOYER ACTIONS
  // ==========================================
  const handleAddOwner = async (e) => {
    e.preventDefault();
    if (!newOwnerAddress || !ethers.isAddress(newOwnerAddress)) {
      return toast.error('Please enter a valid new owner address');
    }
    setIsDeployerActionLoading(true);
    try {
      const contract = await getSignerContract();
      toast.info('Adding new owner...');
      const tx = await contract.addOwner(newOwnerAddress);
      await tx.wait();
      toast.success(`New owner ${shortenAddress(newOwnerAddress)} added successfully!`);
      setNewOwnerAddress('');
      await fetchContractState();
    } catch (err) {
      toast.error(err.reason || err.message || 'Add owner failed');
    } finally {
      setIsDeployerActionLoading(false);
    }
  };

  const handleRemoveOwner = async (e) => {
    e.preventDefault();
    if (!removeOwnerAddress || !ethers.isAddress(removeOwnerAddress)) {
      return toast.error('Please select an owner address to remove');
    }
    setIsDeployerActionLoading(true);
    try {
      const contract = await getSignerContract();
      toast.info('Removing owner...');
      const tx = await contract.removeOwner(removeOwnerAddress);
      await tx.wait();
      toast.success(`Owner ${shortenAddress(removeOwnerAddress)} removed!`);
      setRemoveOwnerAddress('');
      await fetchContractState();
    } catch (err) {
      toast.error(err.reason || err.message || 'Remove owner failed');
    } finally {
      setIsDeployerActionLoading(false);
    }
  };

  const handleChangeRequirement = async (e) => {
    e.preventDefault();
    const reqNum = parseInt(newRequirementNumber, 10);
    if (isNaN(reqNum) || reqNum <= 0 || reqNum > ownersList.length) {
      return toast.error(`Requirement must be between 1 and total owners (${ownersList.length})`);
    }
    setIsDeployerActionLoading(true);
    try {
      const contract = await getSignerContract();
      toast.info(`Updating required confirmations to ${reqNum}...`);
      const tx = await contract.changeRequirement(reqNum);
      await tx.wait();
      toast.success(`Required confirmations updated to ${reqNum}!`);
      setNewRequirementNumber('');
      await fetchContractState();
    } catch (err) {
      toast.error(err.reason || err.message || 'Change requirement failed');
    } finally {
      setIsDeployerActionLoading(false);
    }
  };

  const handleChangeDeployer = async (e) => {
    e.preventDefault();
    if (!newDeployerAddress || !ethers.isAddress(newDeployerAddress)) {
      return toast.error('Please enter a valid new deployer address');
    }
    if (!window.confirm('WARNING: Are you sure you want to transfer deployer role? You will lose deployer management privileges.')) {
      return;
    }
    setIsDeployerActionLoading(true);
    try {
      const contract = await getSignerContract();
      toast.info('Transferring deployer role...');
      const tx = await contract.changeDeployer(newDeployerAddress);
      await tx.wait();
      toast.success(`Deployer successfully updated to ${shortenAddress(newDeployerAddress)}!`);
      setNewDeployerAddress('');
      await fetchContractState();
    } catch (err) {
      toast.error(err.reason || err.message || 'Change deployer failed');
    } finally {
      setIsDeployerActionLoading(false);
    }
  };

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <h4 className="page-title fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                  <i className="fa fa-university text-warning fs-22"></i>
                  MultiSig Treasury Smart Contract
                  {!canEdit && (
                    <span className="badge bg-warning-subtle text-warning border border-warning-subtle fs-11 fw-normal">
                      <i className="fa fa-eye me-1"></i> Viewer Mode (Read-Only)
                    </span>
                  )}
                </h4>
                <p className="text-muted fs-13 mb-0">
                  Binance Smart Chain (BSC) Multi-Signature Vault &middot; Contract:{' '}
                  <span className="font-monospace text-dark fw-semibold me-1">
                    {shortenAddress(TREASURY_CONTRACT_ADDRESS)}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 text-muted me-2"
                    onClick={() => copyText(TREASURY_CONTRACT_ADDRESS, 'Contract Address')}
                    title="Copy full contract address"
                  >
                    <i className="fa fa-copy"></i>
                  </button>
                  <a
                    href={`${BSC_EXPLORER_URL}/address/${TREASURY_CONTRACT_ADDRESS}`}
                    target="_blank"
                    rel="noreferrer"
                    className="badge bg-light text-primary border text-decoration-none me-2"
                  >
                    <i className="fa fa-external-link-alt me-1"></i> BscScan
                  </a>
                  &middot; Reserves:
                  <span className="badge bg-warning-subtle text-dark border border-warning-subtle ms-1 font-monospace">
                    {Number(bnbBalance).toFixed(4)} BNB
                  </span>
                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle ms-1 font-monospace">
                    {parseFloat(nmrcBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} NMRC
                  </span>
                </p>
              </div>

              {/* Wallet Connection / Status Bar */}
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mt-md-0">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={fetchContractState}
                  disabled={loadingContractData}
                  title="Reload contract data from BSC"
                >
                  <i className={`fa fa-sync-alt ${loadingContractData ? 'fa-spin' : ''}`}></i>
                  <span>Refresh</span>
                </button>

                {account ? (
                  <div className="d-flex align-items-center gap-2">
                    {/* Active Account Pill */}
                    <div className="d-flex align-items-center gap-2 px-3 py-1 bg-white border rounded-pill shadow-sm">
                      <span className="badge bg-success rounded-circle p-1"></span>
                      <span className="font-monospace fw-bold text-dark fs-12">{shortenAddress(account)}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-muted"
                        onClick={() => copyText(account, 'Connected Address')}
                        title="Copy address"
                      >
                        <i className="fa fa-copy fs-11"></i>
                      </button>
                      <a
                        href={`${BSC_EXPLORER_URL}/address/${account}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary fs-11"
                        title="View on BscScan"
                      >
                        <i className="fa fa-external-link-alt"></i>
                      </a>
                      {isDeployerOfContract && (
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle fs-10">
                          Deployer
                        </span>
                      )}
                      {isOwnerOfContract && (
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle fs-10">
                          Owner
                        </span>
                      )}
                      {chainId === BSC_CHAIN_ID_DECIMAL ? (
                        <span className="badge bg-warning text-dark fs-10">BSC 56</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-xs btn-warning py-0 px-2 fs-10 fw-bold border-0"
                          onClick={switchWalletToBsc}
                          title="Switch to BSC Mainnet"
                        >
                          Switch BSC
                        </button>
                      )}
                    </div>

                    {/* Direct Disconnect Button */}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-3 shadow-sm fw-semibold"
                      onClick={disconnectWallet}
                      title="Disconnect active wallet"
                    >
                      <i className="fa fa-power-off"></i>
                      <span>Disconnect</span>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary d-flex align-items-center gap-2 px-3 shadow-sm fw-semibold"
                    onClick={connectWallet}
                    disabled={isConnecting}
                  >
                    <i className="fa fa-wallet"></i>
                    <span>{isConnecting ? 'Connecting...' : 'Connect BSC Wallet'}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Dedicated Wallet Status & Action Card */}
            {!account ? (
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-primary-gradient text-white">
                <div className="card-body p-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-white bg-opacity-20 p-3">
                      <i className="fa fa-wallet fs-24 text-white"></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1 text-white">Wallet is Disconnected</h6>
                      <p className="mb-0 fs-12 op-9">
                        Connect MetaMask, Trust Wallet, or any Web3 wallet to submit proposals, confirm transactions, and execute MultiSig actions.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-light text-primary fw-bold px-4 py-2 shadow-sm d-flex align-items-center gap-2"
                    onClick={connectWallet}
                    disabled={isConnecting}
                  >
                    <i className="fa fa-plug"></i>
                    <span>{isConnecting ? 'Connecting...' : 'Connect Wallet'}</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
              </>
            )}

            {/* Network Warning Banner */}
            {account && chainId !== BSC_CHAIN_ID_DECIMAL && (
              <div className="alert alert-warning d-flex align-items-center justify-content-between p-3 rounded-3 mb-4 shadow-sm">
                <div className="d-flex align-items-center gap-2">
                  <i className="fa fa-exclamation-triangle fs-20 text-warning"></i>
                  <div>
                    <strong>Incorrect Network Detected:</strong> Your wallet is currently on Chain #{chainId || 'Unknown'}. Please switch to <strong>Binance Smart Chain (Chain ID: 56)</strong> to perform write transactions.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-warning text-dark fw-bold px-3"
                  onClick={switchWalletToBsc}
                >
                  Switch Network
                </button>
              </div>
            )}

            {/* Read/Write Status Notice */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between p-2 px-3 bg-light rounded-3 border fs-12">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      <i className="fa fa-circle me-1 fs-8"></i> Live BSC RPC
                    </span>
                    <span className="text-muted">
                      All Read Operations are public and synched from Binance Smart Chain directly.
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
                    <span className="text-muted">Write Transactions:</span>
                    {account ? (
                      isOwnerOfContract || isDeployerOfContract ? (
                        <span className="badge bg-success text-white">
                          <i className="fa fa-check me-1"></i> Authorized ({isDeployerOfContract ? 'Deployer' : 'Owner'})
                        </span>
                      ) : (
                        <span className="badge bg-warning text-dark">
                          Connected (Non-Owner: Read & Deposit Only)
                        </span>
                      )
                    ) : (
                      <span className="badge bg-secondary text-white">
                        <i className="fa fa-lock me-1"></i> Wallet Connect Required for Write
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Primary KPI Metrics */}
            <div className="row g-3 mb-4">
              {/* Card 1: BNB Balance */}
              <div className={canEdit ? "col-xl col-lg-4 col-md-6 col-12" : "col-xl-3 col-lg-6 col-md-6 col-12"}>
                <div className="card border-0 shadow-sm rounded-3 h-100">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">Contract BNB Balance</span>
                        <h3 className="fw-bold mb-0 text-dark mt-1 font-monospace">
                          {Number(bnbBalance).toFixed(4)} <span className="fs-14 fw-normal text-muted">BNB</span>
                        </h3>
                        <span className="text-muted fs-11">Native Vault Liquidity</span>
                      </div>
                      <div className="p-3 bg-warning-subtle text-warning rounded-3">
                        <i className="fa fa-coins fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: NMRC Balance */}
              <div className={canEdit ? "col-xl col-lg-4 col-md-6 col-12" : "col-xl-3 col-lg-6 col-md-6 col-12"}>
                <div className="card border-0 shadow-sm rounded-3 h-100 border-start border-4 border-primary">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">Contract NMRC Balance</span>
                        <h3 className="fw-bold mb-0 text-primary mt-1 font-monospace">
                          {loadingContractData ? (
                            <span className="fs-18 text-muted">Loading...</span>
                          ) : (
                            <>
                              {parseFloat(nmrcBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{' '}
                              <span className="fs-14 fw-normal text-muted">NMRC</span>
                            </>
                          )}
                        </h3>
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <span className="text-muted fs-11">Neo MarcLife Vault</span>
                          &middot;
                          <a
                            href={`${BSC_EXPLORER_URL}/token/${BSC_NMRC_ADDRESS}?a=${TREASURY_CONTRACT_ADDRESS}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary fs-11 text-decoration-none"
                            title="Inspect NMRC on BscScan"
                          >
                            BscScan <i className="fa fa-external-link-alt fs-9"></i>
                          </a>
                        </div>
                      </div>
                      <div className="p-3 bg-primary-subtle text-primary rounded-3">
                        <i className="fa fa-gem fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Required Confirmations */}
              <div className={canEdit ? "col-xl col-lg-4 col-md-6 col-12" : "col-xl-3 col-lg-6 col-md-6 col-12"}>
                <div className="card border-0 shadow-sm rounded-3 h-100">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">MultiSig Quorum</span>
                        <h3 className="fw-bold mb-0 text-primary mt-1 font-monospace">
                          {requiredConfirmations} <span className="fs-14 fw-normal text-muted">/ {ownersList.length} Owners</span>
                        </h3>
                        <span className="text-muted fs-11">
                          {ownersList.length > 0
                            ? `${((requiredConfirmations / ownersList.length) * 100).toFixed(0)}% Consensus Required`
                            : 'Loading...'}
                        </span>
                      </div>
                      <div className="p-3 bg-primary-subtle text-primary rounded-3">
                        <i className="fa fa-users-cog fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Total Transactions */}
              <div className={canEdit ? "col-xl col-lg-6 col-md-6 col-12" : "col-xl-3 col-lg-6 col-md-6 col-12"}>
                <div className="card border-0 shadow-sm rounded-3 h-100">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">Total Tx Submitted</span>
                        <h3 className="fw-bold mb-0 text-info mt-1 font-monospace">
                          {transactionCount}
                        </h3>
                        <span className="text-muted fs-11">
                          {transactions.filter(t => t.executed).length} Executed &middot;{' '}
                          {transactions.filter(t => !t.executed).length} Pending
                        </span>
                      </div>
                      <div className="p-3 bg-info-subtle text-info rounded-3">
                        <i className="fa fa-list-check fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 5: Deployer Authority (Visible only for Editor / Non-Viewer) */}
              {canEdit && (
                <div className="col-xl col-lg-6 col-md-6 col-12">
                  <div className="card border-0 shadow-sm rounded-3 h-100">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span className="text-muted fs-11 text-uppercase fw-semibold">Deployer Authority</span>
                          <div className="fw-bold mb-0 text-dark mt-1 font-monospace fs-14">
                            {contractDeployer ? shortenAddress(contractDeployer) : 'Loading...'}
                          </div>
                          <div className="d-flex align-items-center gap-1 mt-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-muted fs-11"
                              onClick={() => copyText(contractDeployer, 'Deployer Address')}
                            >
                              <i className="fa fa-copy me-1"></i> Copy
                            </button>
                            &middot;
                            <a
                              href={`${BSC_EXPLORER_URL}/address/${contractDeployer}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary fs-11 text-decoration-none"
                            >
                              BscScan
                            </a>
                          </div>
                        </div>
                        <div className="p-3 bg-danger-subtle text-danger rounded-3">
                          <i className="fa fa-crown fs-24"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Tabs Bar */}
            <div className="card border-0 shadow-sm rounded-3 mb-4">
              <div className="card-body p-2">
                <ul className="nav nav-pills nav-fill flex-column flex-sm-row gap-1">
                  <li className="nav-item">
                    <button
                      className={`nav-link py-2 fs-13 fw-semibold ${activeTab === 'transactions' ? 'active' : ''}`}
                      onClick={() => setActiveTab('transactions')}
                    >
                      <i className="fa fa-tasks me-2"></i>
                      Transactions Explorer ({transactionCount})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link py-2 fs-13 fw-semibold ${activeTab === 'submit' ? 'active' : ''}`}
                      onClick={() => setActiveTab('submit')}
                    >
                      <i className="fa fa-paper-plane me-2"></i>
                      Submit New Tx
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link py-2 fs-13 fw-semibold ${activeTab === 'owners' ? 'active' : ''}`}
                      onClick={() => setActiveTab('owners')}
                    >
                      <i className="fa fa-users me-2"></i>
                      Owners Directory ({ownersList.length})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link py-2 fs-13 fw-semibold ${activeTab === 'tokenCheck' ? 'active' : ''}`}
                      onClick={() => setActiveTab('tokenCheck')}
                    >
                      <i className="fa fa-search-dollar me-2"></i>
                      Token Balance Checker
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link py-2 fs-13 fw-semibold ${activeTab === 'deposit' ? 'active' : ''}`}
                      onClick={() => setActiveTab('deposit')}
                    >
                      <i className="fa fa-donate me-2"></i>
                      Deposit BNB
                    </button>
                  </li>
                  {canEdit && (
                    <li className="nav-item">
                      <button
                        className={`nav-link py-2 fs-13 fw-semibold ${activeTab === 'deployer' ? 'active' : ''}`}
                        onClick={() => setActiveTab('deployer')}
                      >
                        <i className="fa fa-shield-alt me-2"></i>
                        Deployer Hub
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* ==================================================== */}
            {/* TAB 1: TRANSACTIONS EXPLORER */}
            {/* ==================================================== */}
            {activeTab === 'transactions' && (
              <div className="card border-0 shadow-sm rounded-3 mb-4">
                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="card-title mb-0 fw-bold text-dark">
                      <i className="fa fa-list-ol text-primary me-2"></i>
                      MultiSig Transactions Queue & History
                    </h6>
                    <span className="text-muted fs-12">
                      Inspect pending and executed transactions &middot; Confirm, Revoke, or Execute
                    </span>
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => setActiveTab('submit')}
                    >
                      <i className="fa fa-plus me-1"></i> Submit Transaction
                    </button>
                  )}
                </div>

                <div className="card-body p-0">
                  {loadingContractData ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2 fs-13">Loading MultiSig transactions from BSC...</p>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fa fa-folder-open fs-36 text-muted mb-2"></i>
                      <h6 className="text-dark fw-bold">No Transactions Found</h6>
                      <p className="text-muted fs-13 mb-3">
                        No transactions have been submitted to this MultiSig Treasury yet.
                      </p>
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => setActiveTab('submit')}
                        >
                          <i className="fa fa-plus me-1"></i> Submit First Transaction
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0 text-nowrap">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '60px' }}>Tx ID</th>
                            <th>Type</th>
                            <th>Recipient</th>
                            <th>Amount</th>
                            <th>Confirmations</th>
                            <th>Status</th>
                            <th>Your Confirmation</th>
                            <th className="text-end" style={{ width: '180px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx) => {
                            const isEnoughConfirmations = tx.numConfirmations >= requiredConfirmations;
                            const isActionLoading = actionLoadingTxId === tx.txId;

                            return (
                              <tr key={tx.txId}>
                                <td>
                                  <span className="badge bg-light text-dark font-monospace border fs-12">
                                    #{tx.txId}
                                  </span>
                                </td>
                                <td>
                                  {tx.isBnb ? (
                                    <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                                      <i className="fa fa-coins me-1"></i> BNB Transfer
                                    </span>
                                  ) : tx.token && tx.token.toLowerCase() === BSC_NMRC_ADDRESS.toLowerCase() ? (
                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                                      <i className="fa fa-gem me-1"></i> NMRC Token
                                    </span>
                                  ) : tx.token && tx.token.toLowerCase() === BSC_USDT_ADDRESS.toLowerCase() ? (
                                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                                      <i className="fa fa-dollar-sign me-1"></i> USDT Token
                                    </span>
                                  ) : (
                                    <span className="badge bg-info-subtle text-info border border-info-subtle">
                                      <i className="fa fa-coins me-1"></i> BEP20 Token
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1 font-monospace fs-13">
                                    <span>{shortenAddress(tx.to)}</span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-muted"
                                      onClick={() => copyText(tx.to, 'Recipient Address')}
                                      title="Copy address"
                                    >
                                      <i className="fa fa-copy fs-11"></i>
                                    </button>
                                    <a
                                      href={`${BSC_EXPLORER_URL}/address/${tx.to}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-muted"
                                      title="View on BscScan"
                                    >
                                      <i className="fa fa-external-link-alt fs-11"></i>
                                    </a>
                                  </div>
                                </td>
                                <td>
                                  {tx.isBnb ? (
                                    <span className="fw-bold text-dark font-monospace">
                                      {ethers.formatEther(tx.value)} BNB
                                    </span>
                                  ) : (
                                    <div>
                                      <span className="fw-bold text-dark font-monospace">
                                        {ethers.formatUnits(tx.tokenAmount, 18)}{' '}
                                        <span className="fs-11 fw-semibold text-muted">
                                          {tx.token && tx.token.toLowerCase() === BSC_NMRC_ADDRESS.toLowerCase()
                                            ? 'NMRC'
                                            : tx.token && tx.token.toLowerCase() === BSC_USDT_ADDRESS.toLowerCase()
                                              ? 'USDT'
                                              : 'Tokens'}
                                        </span>
                                      </span>
                                      <div className="fs-10 text-muted font-monospace">
                                        Token: {shortenAddress(tx.token)}
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div style={{ minWidth: '130px' }}>
                                    <div className="d-flex justify-content-between fs-11 mb-1">
                                      <span className="fw-semibold">
                                        {tx.numConfirmations} / {requiredConfirmations}
                                      </span>
                                      <span className="text-muted">
                                        {((tx.numConfirmations / (requiredConfirmations || 1)) * 100).toFixed(0)}%
                                      </span>
                                    </div>
                                    <div className="progress" style={{ height: '6px' }}>
                                      <div
                                        className={`progress-bar ${tx.executed
                                          ? 'bg-secondary'
                                          : isEnoughConfirmations
                                            ? 'bg-success'
                                            : 'bg-primary'
                                          }`}
                                        role="progressbar"
                                        style={{
                                          width: `${Math.min(
                                            (tx.numConfirmations / (requiredConfirmations || 1)) * 100,
                                            100
                                          )}%`
                                        }}
                                      ></div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  {tx.executed ? (
                                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                                      <i className="fa fa-check-circle me-1"></i> Executed
                                    </span>
                                  ) : isEnoughConfirmations ? (
                                    <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                                      <i className="fa fa-hourglass-half me-1"></i> Ready to Execute
                                    </span>
                                  ) : (
                                    <span className="badge bg-light text-muted border">
                                      Awaiting Confirmations
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {!account ? (
                                    <span className="text-muted fs-12">Connect wallet</span>
                                  ) : tx.userConfirmed ? (
                                    <span className="badge bg-success-subtle text-success border border-success-subtle fs-11">
                                      <i className="fa fa-check me-1"></i> Confirmed
                                    </span>
                                  ) : (
                                    <span className="badge bg-light text-muted border fs-11">
                                      Not Confirmed
                                    </span>
                                  )}
                                </td>
                                <td className="text-end">
                                  {tx.executed ? (
                                    <span className="text-muted fs-12 fst-italic">Completed</span>
                                  ) : !canEdit ? (
                                    <span className="badge bg-light text-muted border fs-11">
                                      <i className="fa fa-lock me-1"></i> View Only
                                    </span>
                                  ) : (
                                    <div className="d-flex justify-content-end gap-1">
                                      {/* Confirm / Revoke Buttons for Owner */}
                                      {isOwnerOfContract && (
                                        <>
                                          {!tx.userConfirmed ? (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-success"
                                              onClick={() => handleConfirmTransaction(tx.txId)}
                                              disabled={isActionLoading}
                                              title="Confirm this transaction"
                                            >
                                              {isActionLoading ? (
                                                <i className="fa fa-spinner fa-spin"></i>
                                              ) : (
                                                <>
                                                  <i className="fa fa-check me-1"></i> Confirm
                                                </>
                                              )}
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger"
                                              onClick={() => handleRevokeConfirmation(tx.txId)}
                                              disabled={isActionLoading}
                                              title="Revoke your confirmation"
                                            >
                                              {isActionLoading ? (
                                                <i className="fa fa-spinner fa-spin"></i>
                                              ) : (
                                                <>
                                                  <i className="fa fa-times me-1"></i> Revoke
                                                </>
                                              )}
                                            </button>
                                          )}
                                        </>
                                      )}

                                      {/* Execute Button */}
                                      {isEnoughConfirmations && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() => handleExecuteTransaction(tx.txId)}
                                          disabled={isActionLoading || (!isOwnerOfContract && !isDeployerOfContract)}
                                          title="Execute this transaction on blockchain"
                                        >
                                          {isActionLoading ? (
                                            <i className="fa fa-spinner fa-spin"></i>
                                          ) : (
                                            <>
                                              <i className="fa fa-bolt me-1"></i> Execute
                                            </>
                                          )}
                                        </button>
                                      )}

                                      {!isOwnerOfContract && !isEnoughConfirmations && (
                                        <span className="text-muted fs-11">Owner action</span>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ==================================================== */}
            {/* TAB 2: SUBMIT NEW TRANSACTION */}
            {/* ==================================================== */}
            {activeTab === 'submit' && (
              <div className="row justify-content-center">
                <div className="col-lg-8 col-12">
                  <div className="card border-0 shadow-sm rounded-3 mb-4">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-paper-plane text-primary me-2"></i>
                        Submit MultiSig Transaction Proposal
                      </h6>
                      <span className="text-muted fs-12">
                        Only registered MultiSig Owners can submit proposals. Requires {requiredConfirmations} owner confirmations to execute.
                      </span>
                    </div>

                    <div className="card-body p-4">
                      {!canEdit ? (
                        <div className="alert alert-warning text-center p-4">
                          <i className="fa fa-eye fs-32 text-warning mb-2"></i>
                          <h6 className="fw-bold text-dark">Viewer Admin Mode (Read-Only)</h6>
                          <p className="text-muted fs-13 mb-0">
                            Your admin account has read-only access to the Treasury module. Proposal submission is disabled.
                          </p>
                        </div>
                      ) : !account ? (
                        <div className="alert alert-secondary text-center p-4">
                          <i className="fa fa-wallet fs-32 text-muted mb-2"></i>
                          <h6 className="fw-bold text-dark">Wallet Not Connected</h6>
                          <p className="text-muted fs-13 mb-3">
                            Connect your Web3 wallet to submit proposals to the MultiSig Treasury.
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={connectWallet}
                          >
                            <i className="fa fa-wallet me-1"></i> Connect BSC Wallet
                          </button>
                        </div>
                      ) : !isOwnerOfContract ? (
                        <div className="alert alert-warning p-3">
                          <i className="fa fa-exclamation-circle me-2"></i>
                          Connected address <strong>{shortenAddress(account)}</strong> is not in the owners list of this contract. Only registered owners can submit proposals.
                        </div>
                      ) : null}

                      {/* Transaction Type Radio Selector */}
                      <div className="mb-4">
                        <label className="form-label fs-13 fw-semibold">Transfer Asset Type</label>
                        <div className="d-flex gap-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="txType"
                              id="txTypeBnb"
                              checked={submitTxType === 'BNB'}
                              onChange={() => setSubmitTxType('BNB')}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="txTypeBnb">
                              <i className="fa fa-coins text-warning me-1"></i> BNB (Native Coin)
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="txType"
                              id="txTypeToken"
                              checked={submitTxType === 'TOKEN'}
                              onChange={() => setSubmitTxType('TOKEN')}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="txTypeToken">
                              <i className="fa fa-gem text-primary me-1"></i> BEP20 Token (NMRC / USDT)
                            </label>
                          </div>
                        </div>
                      </div>

                      <form onSubmit={handleSubmitTransaction}>
                        {/* Recipient Address */}
                        <div className="mb-3">
                          <label className="form-label fs-13 fw-semibold">
                            Recipient Address (to) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            placeholder="0x..."
                            value={submitToAddress}
                            onChange={(e) => setSubmitToAddress(e.target.value.trim())}
                            required
                          />
                          <span className="text-muted fs-11">The destination wallet address to receive funds.</span>
                        </div>

                        {/* BNB Specific Field */}
                        {submitTxType === 'BNB' && (
                          <div className="mb-3">
                            <label className="form-label fs-13 fw-semibold">
                              BNB Amount <span className="text-danger">*</span>
                            </label>
                            <div className="input-group input-group-sm">
                              <input
                                type="number"
                                step="any"
                                className="form-control font-monospace"
                                placeholder="0.0"
                                value={submitBnbAmount}
                                onChange={(e) => setSubmitBnbAmount(e.target.value)}
                                required
                              />
                              <span className="input-group-text bg-light">BNB</span>
                            </div>
                            <span className="text-muted fs-11">
                              Current Treasury Balance: <strong>{Number(bnbBalance).toFixed(4)} BNB</strong>
                            </span>
                          </div>
                        )}

                        {/* Token Specific Fields */}
                        {submitTxType === 'TOKEN' && (
                          <>
                            <div className="mb-3">
                              <label className="form-label fs-13 fw-semibold">
                                ERC20 / BEP20 Token Contract Address <span className="text-danger">*</span>
                              </label>
                              <div className="input-group input-group-sm">
                                <input
                                  type="text"
                                  className="form-control font-monospace"
                                  placeholder="0x..."
                                  value={submitTokenAddress}
                                  onChange={(e) => setSubmitTokenAddress(e.target.value.trim())}
                                  required
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => {
                                    setSubmitTokenAddress(BSC_NMRC_ADDRESS);
                                    setSubmitTokenDecimals(18);
                                  }}
                                >
                                  Preset NMRC
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => {
                                    setSubmitTokenAddress(BSC_USDT_ADDRESS);
                                    setSubmitTokenDecimals(18);
                                  }}
                                >
                                  Preset BSC USDT
                                </button>
                              </div>
                              {submitTokenAddress && submitTokenAddress.toLowerCase() === BSC_NMRC_ADDRESS.toLowerCase() && (
                                <span className="text-primary fs-11 mt-1 d-block fw-semibold">
                                  <i className="fa fa-info-circle me-1"></i> Current Treasury NMRC Balance: {parseFloat(nmrcBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} NMRC
                                </span>
                              )}
                            </div>

                            <div className="row g-2 mb-3">
                              <div className="col-8">
                                <label className="form-label fs-13 fw-semibold">
                                  Token Amount <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="number"
                                  step="any"
                                  className="form-control form-control-sm font-monospace"
                                  placeholder="0.0"
                                  value={submitTokenAmount}
                                  onChange={(e) => setSubmitTokenAmount(e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-4">
                                <label className="form-label fs-13 fw-semibold">Decimals</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={submitTokenDecimals}
                                  onChange={(e) => setSubmitTokenDecimals(Number(e.target.value))}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {/* Calldata */}
                        <div className="mb-4">
                          <label className="form-label fs-13 fw-semibold">
                            Optional Call Data (hex)
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            value={submitData}
                            onChange={(e) => setSubmitData(e.target.value.trim())}
                            placeholder="0x"
                          />
                          <span className="text-muted fs-11">Default is 0x for standard coin/token transfers.</span>
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setActiveTab('transactions')}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-sm btn-primary px-4"
                            disabled={!canEdit || isSubmittingTx || !account || !isOwnerOfContract}
                          >
                            {isSubmittingTx ? (
                              <>
                                <i className="fa fa-spinner fa-spin me-1"></i> Submitting...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-check me-1"></i> Submit Proposal
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ==================================================== */}
            {/* TAB 3: OWNERS DIRECTORY */}
            {/* ==================================================== */}
            {activeTab === 'owners' && (
              <div className="row g-4">
                <div className="col-lg-8 col-12">
                  <div className="card border-0 shadow-sm rounded-3">
                    <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="card-title mb-0 fw-bold text-dark">
                          <i className="fa fa-users text-primary me-2"></i>
                          MultiSig Owners Directory ({ownersList.length})
                        </h6>
                        <span className="text-muted fs-12">
                          Owners authorized to submit proposals and cast confirmation votes
                        </span>
                      </div>
                      <span className="badge bg-primary fs-11">
                        Quorum: {requiredConfirmations} of {ownersList.length}
                      </span>
                    </div>

                    <div className="card-body p-0">
                      {ownersList.length === 0 ? (
                        <div className="text-center py-5 text-muted">No owners loaded.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th>Owner Address</th>
                                <th>Role</th>
                                <th className="text-end" style={{ width: '120px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ownersList.map((ownerAddr, index) => {
                                const isCurrentAccount = account && account.toLowerCase() === ownerAddr.toLowerCase();
                                const isDeployer = contractDeployer && contractDeployer.toLowerCase() === ownerAddr.toLowerCase();

                                return (
                                  <tr key={ownerAddr}>
                                    <td>
                                      <span className="badge bg-light text-muted border">
                                        {index + 1}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="font-monospace fw-semibold text-dark fs-13">
                                          {ownerAddr}
                                        </span>
                                        {isCurrentAccount && (
                                          <span className="badge bg-success text-white fs-10">
                                            You
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle fs-10">
                                          Owner
                                        </span>
                                        {isDeployer && (
                                          <span className="badge bg-danger-subtle text-danger border border-danger-subtle fs-10">
                                            Deployer
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="text-end">
                                      <div className="btn-group btn-group-sm">
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => copyText(ownerAddr, 'Owner Address')}
                                          title="Copy address"
                                        >
                                          <i className="fa fa-copy"></i>
                                        </button>
                                        <a
                                          href={`${BSC_EXPLORER_URL}/address/${ownerAddr}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="btn btn-outline-primary"
                                          title="View on BscScan"
                                        >
                                          <i className="fa fa-external-link-alt"></i>
                                        </a>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Owner Verification Query Tool */}
                <div className="col-lg-4 col-12">
                  <div className="card border-0 shadow-sm rounded-3">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-user-check text-success me-2"></i>
                        Verify Address Ownership
                      </h6>
                      <span className="text-muted fs-12">
                        Query smart contract <code>isOwner(address)</code> function
                      </span>
                    </div>
                    <div className="card-body p-3">
                      <form onSubmit={handleVerifyOwner}>
                        <div className="mb-3">
                          <label className="form-label fs-12 fw-semibold">Target Address</label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            placeholder="0x..."
                            value={verifyOwnerAddress}
                            onChange={(e) => setVerifyOwnerAddress(e.target.value.trim())}
                            required
                          />
                        </div>
                        <button
                          type="submit"
                          className="btn btn-sm btn-outline-primary w-100 mb-3"
                          disabled={isVerifyingOwner}
                        >
                          {isVerifyingOwner ? (
                            <>
                              <i className="fa fa-spinner fa-spin me-1"></i> Querying...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-search me-1"></i> Check isOwner()
                            </>
                          )}
                        </button>
                      </form>

                      {verifyOwnerResult && (
                        <div
                          className={`alert ${verifyOwnerResult.isOwner ? 'alert-success' : 'alert-danger'
                            } p-3 mb-0`}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className={`fa ${verifyOwnerResult.isOwner ? 'fa-check-circle' : 'fa-times-circle'
                                } fs-20`}
                            ></i>
                            <div>
                              <div className="fw-bold fs-13">
                                {verifyOwnerResult.isOwner
                                  ? 'Verified: Address is an Owner!'
                                  : 'Not an Owner'}
                              </div>
                              <div className="font-monospace fs-11 mt-1 text-truncate" style={{ maxWidth: '240px' }}>
                                {verifyOwnerResult.address}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ==================================================== */}
            {/* TAB 4: TOKEN BALANCE CHECKER */}
            {/* ==================================================== */}
            {activeTab === 'tokenCheck' && (
              <div className="row justify-content-center">
                <div className="col-lg-8 col-12">
                  <div className="card border-0 shadow-sm rounded-3">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-search-dollar text-info me-2"></i>
                        ERC20 / BEP20 Token Balance Checker
                      </h6>
                      <span className="text-muted fs-12">
                        Query <code>getTokenBalance(_token)</code> on the MultiSig Treasury contract
                      </span>
                    </div>

                    <div className="card-body p-4">
                      <form onSubmit={handleCheckTokenBalance}>
                        <div className="mb-3">
                          <label className="form-label fs-13 fw-semibold">Token Contract Address</label>
                          <div className="input-group input-group-sm">
                            <input
                              type="text"
                              className="form-control font-monospace"
                              placeholder="0x..."
                              value={tokenCheckAddress}
                              onChange={(e) => setTokenCheckAddress(e.target.value.trim())}
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => setTokenCheckAddress(BSC_NMRC_ADDRESS)}
                            >
                              NMRC Token
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => setTokenCheckAddress(BSC_USDT_ADDRESS)}
                            >
                              BSC USDT
                            </button>
                          </div>
                          <span className="text-muted fs-11">
                            Enter any BEP20 token address deployed on Binance Smart Chain
                          </span>
                        </div>

                        <button
                          type="submit"
                          className="btn btn-sm btn-primary px-4"
                          disabled={isCheckingToken}
                        >
                          {isCheckingToken ? (
                            <>
                              <i className="fa fa-spinner fa-spin me-1"></i> Checking Balance...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-search me-1"></i> Query Treasury Token Balance
                            </>
                          )}
                        </button>
                      </form>

                      {tokenCheckResult && (
                        <div className="mt-4 p-3 bg-light rounded-3 border">
                          <h6 className="fw-bold text-dark mb-2">Query Results:</h6>
                          <div className="row g-2 fs-13">
                            <div className="col-sm-4 text-muted">Token Address:</div>
                            <div className="col-sm-8 font-monospace fw-semibold text-dark">
                              {tokenCheckResult.address}
                            </div>
                            <div className="col-sm-4 text-muted">Symbol / Name:</div>
                            <div className="col-sm-8 fw-semibold text-dark">
                              {tokenCheckResult.symbol} ({tokenCheckResult.decimals} decimals)
                            </div>
                            <div className="col-sm-4 text-muted">Treasury Balance:</div>
                            <div className="col-sm-8 font-monospace fs-16 fw-bold text-success">
                              {tokenCheckResult.formatted} {tokenCheckResult.symbol}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ==================================================== */}
            {/* TAB 5: DIRECT BNB DEPOSIT */}
            {/* ==================================================== */}
            {activeTab === 'deposit' && (
              <div className="row justify-content-center">
                <div className="col-lg-6 col-12">
                  <div className="card border-0 shadow-sm rounded-3">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-donate text-warning me-2"></i>
                        Deposit BNB to Treasury Vault
                      </h6>
                      <span className="text-muted fs-12">
                        Triggers contract <code>receive() external payable</code> to fund the vault
                      </span>
                    </div>

                    <div className="card-body p-4">
                      <form onSubmit={handleDepositBnb}>
                        <div className="mb-3">
                          <label className="form-label fs-13 fw-semibold">
                            Treasury Recipient Contract
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace bg-light"
                            value={TREASURY_CONTRACT_ADDRESS}
                            readOnly
                          />
                        </div>

                        <div className="mb-4">
                          <label className="form-label fs-13 fw-semibold">
                            BNB Amount to Send <span className="text-danger">*</span>
                          </label>
                          <div className="input-group input-group-sm">
                            <input
                              type="number"
                              step="any"
                              className="form-control font-monospace"
                              placeholder="0.05"
                              value={depositAmount}
                              onChange={(e) => setDepositAmount(e.target.value)}
                              required
                            />
                            <span className="input-group-text bg-light">BNB</span>
                          </div>
                          <span className="text-muted fs-11">
                            Funds will be locked in the MultiSig contract until released via consensus.
                          </span>
                        </div>

                        <button
                          type="submit"
                          className="btn btn-sm btn-warning text-dark fw-bold w-100 py-2"
                          disabled={isDepositingBnb || !account}
                        >
                          {isDepositingBnb ? (
                            <>
                              <i className="fa fa-spinner fa-spin me-1"></i> Processing Deposit...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-wallet me-1"></i> Send BNB to Treasury
                            </>
                          )}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ==================================================== */}
            {/* TAB 6: DEPLOYER HUB */}
            {/* ==================================================== */}
            {activeTab === 'deployer' && canEdit && (
              <div className="row g-4">
                <div className="col-12">
                  <div className="alert alert-info d-flex align-items-center gap-2 p-3 rounded-3 mb-0">
                    <i className="fa fa-info-circle fs-20 text-info"></i>
                    <div>
                      <strong>Deployer Privilege Zone:</strong> Functions below require calling with the Deployer wallet ({shortenAddress(contractDeployer)}).
                      {isDeployerOfContract ? (
                        <span className="badge bg-success ms-2">You are connected as Deployer</span>
                      ) : (
                        <span className="badge bg-secondary ms-2">Connected wallet is not deployer</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action 1: Add Owner */}
                <div className="col-md-6 col-12">
                  <div className="card border-0 shadow-sm rounded-3 h-100">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-user-plus text-success me-2"></i>
                        Add New MultiSig Owner
                      </h6>
                      <span className="text-muted fs-11"><code>addOwner(address _owner)</code></span>
                    </div>
                    <div className="card-body p-3">
                      <form onSubmit={handleAddOwner}>
                        <div className="mb-3">
                          <label className="form-label fs-12 fw-semibold">New Owner Address</label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            placeholder="0x..."
                            value={newOwnerAddress}
                            onChange={(e) => setNewOwnerAddress(e.target.value.trim())}
                            required
                          />
                        </div>
                        <button
                          type="submit"
                          className="btn btn-sm btn-success w-100"
                          disabled={isDeployerActionLoading || !isDeployerOfContract}
                        >
                          <i className="fa fa-plus-circle me-1"></i> Add Owner
                        </button>
                      </form>
                    </div>
                  </div>
                </div>

                {/* Action 2: Remove Owner */}
                <div className="col-md-6 col-12">
                  <div className="card border-0 shadow-sm rounded-3 h-100">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-user-minus text-danger me-2"></i>
                        Remove Owner
                      </h6>
                      <span className="text-muted fs-11"><code>removeOwner(address _owner)</code></span>
                    </div>
                    <div className="card-body p-3">
                      <form onSubmit={handleRemoveOwner}>
                        <div className="mb-3">
                          <label className="form-label fs-12 fw-semibold">Select Owner to Remove</label>
                          <select
                            className="form-select form-select-sm font-monospace"
                            value={removeOwnerAddress}
                            onChange={(e) => setRemoveOwnerAddress(e.target.value)}
                            required
                          >
                            <option value="">-- Choose Owner Address --</option>
                            {ownersList.map((addr) => (
                              <option key={addr} value={addr}>
                                {addr}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="submit"
                          className="btn btn-sm btn-danger w-100"
                          disabled={isDeployerActionLoading || !isDeployerOfContract || !removeOwnerAddress}
                        >
                          <i className="fa fa-trash-alt me-1"></i> Remove Selected Owner
                        </button>
                      </form>
                    </div>
                  </div>
                </div>

                {/* Action 3: Change Required Confirmations */}
                <div className="col-md-6 col-12">
                  <div className="card border-0 shadow-sm rounded-3 h-100">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-sliders-h text-primary me-2"></i>
                        Change Required Confirmations
                      </h6>
                      <span className="text-muted fs-11"><code>changeRequirement(uint256 _required)</code></span>
                    </div>
                    <div className="card-body p-3">
                      <form onSubmit={handleChangeRequirement}>
                        <div className="mb-3">
                          <label className="form-label fs-12 fw-semibold">
                            New Quorum Requirement (Current: {requiredConfirmations} / {ownersList.length})
                          </label>
                          <input
                            type="number"
                            min="1"
                            max={ownersList.length}
                            className="form-control form-control-sm"
                            placeholder="e.g. 3, 5, etc."
                            value={newRequirementNumber}
                            onChange={(e) => setNewRequirementNumber(e.target.value)}
                            required
                          />
                        </div>
                        <button
                          type="submit"
                          className="btn btn-sm btn-primary w-100"
                          disabled={isDeployerActionLoading || !isDeployerOfContract}
                        >
                          <i className="fa fa-save me-1"></i> Update Requirement
                        </button>
                      </form>
                    </div>
                  </div>
                </div>

                {/* Action 4: Change Deployer */}
                <div className="col-md-6 col-12">
                  <div className="card border-0 shadow-sm rounded-3 h-100">
                    <div className="card-header bg-white py-3 border-bottom">
                      <h6 className="card-title mb-0 fw-bold text-dark">
                        <i className="fa fa-crown text-warning me-2"></i>
                        Transfer Deployer Role
                      </h6>
                      <span className="text-muted fs-11"><code>changeDeployer(address _newDeployer)</code></span>
                    </div>
                    <div className="card-body p-3">
                      <form onSubmit={handleChangeDeployer}>
                        <div className="mb-3">
                          <label className="form-label fs-12 fw-semibold">New Deployer Address</label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            placeholder="0x..."
                            value={newDeployerAddress}
                            onChange={(e) => setNewDeployerAddress(e.target.value.trim())}
                            required
                          />
                        </div>
                        <button
                          type="submit"
                          className="btn btn-sm btn-outline-danger w-100"
                          disabled={isDeployerActionLoading || !isDeployerOfContract}
                        >
                          <i className="fa fa-exchange-alt me-1"></i> Transfer Deployer Role
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </Header>
      <Footer />
    </>
  );
}

export default Treasury;
